import { Component, Inject, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog"
import { MatButtonModule } from "@angular/material/button"
import { MatAutocompleteModule } from "@angular/material/autocomplete"
import { MatCheckboxModule } from "@angular/material/checkbox"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { MatIconModule } from "@angular/material/icon"

import { Recording } from "../../models/recording.model"
import { ProductType } from "../../models/product-type.model"
import { Talent } from "../../models/talent.model"
import { TalentService } from "../../services/talent.service"
import { RecordingService } from "../../services/recording.service"
import { UploadService } from "../../services/upload.service"
import { PermissionService } from "../../services/permission.service"
import { ProfileService } from "../../services/profile.service"
import { ProductTypeService } from "../../services/product-type.service"
import { ProductTypeSelectorComponent } from "../../shared/product-type-selector/product-type-selector.component"
import { collapseRepeatedSpaces } from "../../utils/text"
import { isReplaceableRecordingAudio, REPLACE_AUDIO_ERROR } from "../../utils/audio-file"
import { MAX_SPOT_AUDIO_SECONDS, exceedsMaxSpotAudioSeconds } from "../../utils/spot-length"

export interface RecordingEditDialogData {
  recording: Recording
}

export interface RecordingEditDialogResult {
  success: true
  recording: {
    id: number
    name: string
    talentid?: number
    audiolength?: number
    audio_url?: string
    product_types?: ProductType[]
    is_preemptible_promo?: boolean
    station_id?: number
  }
}

@Component({
  selector: "app-recording-edit-dialog",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatIconModule,
    ProductTypeSelectorComponent,
  ],
  template: `
    <div class="dialog">
      <div class="dialog-header">
        <h2>Edit Recording</h2>
        <button mat-icon-button type="button" (click)="close()" aria-label="Close">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="dialog-body">
        <label class="field-label" for="edit-recording-name">Recording Name</label>
        <input
          id="edit-recording-name"
          class="field-input"
          type="text"
          [(ngModel)]="name"
          (blur)="normalizeName()"
          [disabled]="saving"
          maxlength="200"
        />

        <label class="field-label" for="edit-recording-talent">Talent</label>
        @if (!selectedTalent) {
          <input
            id="edit-recording-talent"
            class="field-input"
            type="text"
            [(ngModel)]="talentInput"
            (ngModelChange)="onTalentInputChange($event)"
            [matAutocomplete]="talentAuto"
            [disabled]="saving || loadingTalents"
            placeholder="Type to search or create new talent"
            autocomplete="off"
          />
          <mat-autocomplete
            #talentAuto="matAutocomplete"
            [displayWith]="displayTalentName"
            (optionSelected)="onTalentSelectionChange($event.option.value)">
            @for (t of filteredTalents; track t.id) {
              <mat-option [value]="t">{{ t.name }}</mat-option>
            }
          </mat-autocomplete>
        }

        @if (selectedTalent) {
          <div class="selected-talent">
            <span class="selected-label"><mat-icon>check_circle</mat-icon> Selected talent</span>
            <div class="talent-chip">
              <mat-icon>person</mat-icon>
              <span>{{ selectedTalent.name }}</span>
              <button
                type="button"
                class="chip-remove"
                (click)="removeSelectedTalent()"
                [disabled]="saving"
                aria-label="Remove talent">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>
        } @else if (talentInput.trim() && !isTalentExisting()) {
          <button type="button" class="btn-secondary btn-sm add-talent-btn" (click)="addNewTalent()" [disabled]="saving">
            <mat-icon aria-hidden="true">person_add</mat-icon>
            Add "{{ talentInput }}"
          </button>
        }

        @if (canEditStaffFields) {
          <app-product-type-selector
            label="Product Types"
            [preselectedIds]="initialProductTypeIds"
            [panelMode]="true"
            [showInlineLoadingBar]="false"
            (selectionChange)="onProductTypesChange($event)">
          </app-product-type-selector>

          <mat-checkbox
            class="preemptible-checkbox"
            [(ngModel)]="isPreemptiblePromo"
            [disabled]="saving">
            Preemptible Promo
          </mat-checkbox>
        }

        @if (canReplaceAudio) {
          <label class="field-label" for="edit-recording-audio">Audio File</label>
          <div class="audio-replace">
            <input
              id="edit-recording-audio"
              class="file-input"
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg"
              [disabled]="saving || uploading"
              (change)="onAudioSelected($event)"
            />
            @if (selectedFileName) {
              <span class="file-name">{{ selectedFileName }}</span>
            }
            @if (uploading) {
              <span class="upload-status">
                <mat-spinner diameter="16"></mat-spinner>
                Uploading…
              </span>
            }
          </div>
          <p class="hint">You can replace the audio file because this recording is not approved yet.</p>
        } @else {
          <p class="hint muted">Audio file cannot be changed after approval (or without uploader permission).</p>
        }

        @if (error) {
          <div class="error" role="alert">{{ error }}</div>
        }
      </div>

      <div class="dialog-actions">
        <button type="button" class="btn-secondary" (click)="close()" [disabled]="saving">Cancel</button>
        <button type="button" class="btn-primary" (click)="save()" [disabled]="saving || uploading || !canSubmit">
          @if (saving) {
            <mat-spinner diameter="18"></mat-spinner>
          }
          Save
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        box-sizing: border-box;
        width: 100%;
        max-width: 100%;
        overflow-x: hidden;
      }

      .dialog {
        box-sizing: border-box;
        width: 100%;
        max-width: 100%;
        padding: 20px 24px 24px;
        color: var(--text-primary);
        overflow-x: hidden;
      }

      .dialog-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 16px;
      }

      .dialog-header h2 {
        margin: 0;
        font-size: 1.2rem;
        font-weight: 600;
        min-width: 0;
      }

      .dialog-body {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 0;
        max-height: min(70vh, 640px);
        overflow-x: hidden;
        overflow-y: auto;
      }

      .field-label {
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--text-secondary);
        margin-top: 8px;
      }

      .field-input,
      .file-input {
        box-sizing: border-box;
        width: 100%;
        max-width: 100%;
        min-width: 0;
        min-height: 2.75rem;
        padding: 0.7rem 0.85rem;
        border: 1px solid var(--border-color);
        border-radius: 0.5rem;
        background: var(--bg-secondary);
        color: var(--text-primary);
        font-size: 0.95rem;
      }

      .file-input {
        padding: 0.55rem 0.85rem;
      }

      .field-input:focus {
        outline: none;
        border-color: var(--primary-color);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color) 20%, transparent);
      }

      .selected-talent {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.625rem;
      }

      .selected-talent .selected-label {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        color: var(--text-primary);
        font-weight: 600;
        font-size: 0.8125rem;
      }

      .selected-talent .selected-label mat-icon {
        color: var(--primary-color);
        font-size: 18px;
        width: 18px;
        height: 18px;
      }

      .talent-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.35rem 0.65rem;
        background: color-mix(in srgb, var(--primary-color) 12%, transparent);
        border: 1px solid color-mix(in srgb, var(--primary-color) 35%, transparent);
        border-radius: 999px;
        font-size: 0.8125rem;
        color: var(--text-primary);
        font-weight: 600;
      }

      .talent-chip mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
        color: var(--primary-color);
      }

      .chip-remove {
        background: none;
        border: none;
        color: var(--text-secondary);
        cursor: pointer;
        padding: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.125rem;
        height: 1.125rem;
        border-radius: 50%;
      }

      .chip-remove:disabled {
        opacity: 0.65;
        cursor: not-allowed;
      }

      .chip-remove:hover:not(:disabled) {
        background: color-mix(in srgb, var(--text-secondary) 18%, transparent);
        color: var(--text-primary);
      }

      .chip-remove mat-icon {
        font-size: 14px;
        width: 14px;
        height: 14px;
        color: inherit;
      }

      .add-talent-btn {
        align-self: flex-start;
        min-width: 0;
        padding: 0 0.85rem;
      }

      app-product-type-selector {
        margin-top: 8px;
      }

      :host ::ng-deep app-product-type-selector .product-type-panel {
        min-height: 140px;
        max-height: min(220px, 32vh);
      }

      .btn-sm {
        min-height: 2.25rem;
        font-size: 0.8125rem;
      }

      .preemptible-checkbox {
        margin: 8px 0 4px;
        width: fit-content;
        max-width: 100%;
      }

      :host ::ng-deep .preemptible-checkbox.mat-mdc-checkbox {
        --mdc-checkbox-unselected-icon-color: var(--text-secondary);
        --mdc-checkbox-unselected-hover-icon-color: var(--primary-color);
        --mdc-checkbox-selected-checkmark-color: #fff;
        --mdc-checkbox-selected-focus-icon-color: var(--primary-color);
        --mdc-checkbox-selected-hover-icon-color: var(--primary-color);
        --mdc-checkbox-selected-icon-color: var(--primary-color);
        --mdc-checkbox-selected-pressed-icon-color: var(--primary-color);
      }

      :host ::ng-deep .preemptible-checkbox .mdc-label {
        color: var(--text-primary) !important;
        font-weight: 600;
      }

      .audio-replace {
        display: flex;
        flex-direction: column;
        gap: 8px;
        min-width: 0;
      }

      .file-name {
        font-size: 0.85rem;
        color: var(--text-secondary);
        overflow-wrap: anywhere;
        word-break: break-word;
      }

      .upload-status {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 0.85rem;
        color: var(--text-secondary);
      }

      .hint {
        margin: 4px 0 0;
        font-size: 0.8rem;
        color: var(--text-secondary);
        line-height: 1.4;
        overflow-wrap: anywhere;
      }

      .hint.muted {
        color: var(--text-muted);
      }

      .error {
        margin-top: 10px;
        padding: 10px 12px;
        border-radius: 8px;
        background: color-mix(in srgb, #ef4444 12%, var(--bg-secondary));
        color: #ef4444;
        font-size: 0.875rem;
        overflow-wrap: anywhere;
      }

      .dialog-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 20px;
        flex-wrap: wrap;
      }

      .btn-primary,
      .btn-secondary {
        min-width: 100px;
        min-height: 2.5rem;
        border-radius: 0.5rem;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }

      .btn-primary {
        border: none;
        background: var(--primary-color);
        color: #fff;
      }

      .btn-primary:disabled {
        opacity: 0.65;
        cursor: not-allowed;
      }

      .btn-secondary {
        background: transparent;
        border: 1px solid var(--border-color);
        color: var(--text-primary);
      }
    `,
  ],
})
export class RecordingEditDialogComponent implements OnInit {
  name = ""
  talentInput = ""
  selectedTalent: Talent | null = null
  talents: Talent[] = []
  filteredTalents: Talent[] = []
  loadingTalents = false
  saving = false
  uploading = false
  error = ""
  selectedFileName = ""
  private audioTmpKey: string | null = null
  private audioLength: number | null = null
  canReplaceAudio = false
  canEditStaffFields = false

  initialProductTypeIds: number[] = []
  selectedProductTypeIds: number[] = []
  private productTypesInitialized = false
  private productTypesDirty = false
  isPreemptiblePromo = false

  constructor(
    private dialogRef: MatDialogRef<RecordingEditDialogComponent, RecordingEditDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: RecordingEditDialogData,
    private talentService: TalentService,
    private recordingService: RecordingService,
    private uploadService: UploadService,
    private perms: PermissionService,
    private profile: ProfileService,
    private productTypeService: ProductTypeService,
  ) {
    const recording = data.recording
    const currentUserId = this.profile.getUserId()
    this.name = recording.name || ""
    this.canReplaceAudio = this.perms.canReplaceRecordingAudio(recording, currentUserId)
    this.canEditStaffFields = this.perms.canEditStaffOnlyFields(recording, currentUserId)
    this.isPreemptiblePromo = !!recording.is_preemptible_promo
    this.initialProductTypeIds = (recording.product_types || [])
      .map((pt) => Number(pt?.id))
      .filter((id) => Number.isFinite(id))
    this.selectedProductTypeIds = [...this.initialProductTypeIds]
  }

  ngOnInit(): void {
    this.loadingTalents = true
    const selectedId = this.data.recording.talentid != null ? Number(this.data.recording.talentid) : null

    this.talentService.getTalents().subscribe({
      next: (catalog) => {
        this.talents = (catalog || [])
          .filter((t) => t?.id != null)
          .map((t) => ({ ...t, id: Number(t.id) }))
          .sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" }),
          )
        this.filteredTalents = this.talents

        if (selectedId != null) {
          const match = this.talents.find((t) => Number(t.id) === selectedId)
          this.selectedTalent = match || { id: selectedId, name: `Talent #${selectedId}` }
          this.talentInput = this.selectedTalent.name
        }
        this.loadingTalents = false
        if (!this.talents.length) {
          this.error = "Unable to load talents."
        }
      },
      error: () => {
        this.loadingTalents = false
        this.error = "Unable to load talents."
      },
    })
  }

  get canSubmit(): boolean {
    return !!this.name.trim() && !!this.selectedTalent
  }

  normalizeName(): void {
    this.name = collapseRepeatedSpaces(this.name)
  }

  displayTalentName(talent: Talent): string {
    return talent ? talent.name : ""
  }

  onTalentInputChange(value: string): void {
    this.talentInput = value
    const searchTerm = value.toLowerCase().trim()
    this.filteredTalents = !searchTerm
      ? this.talents
      : this.talents.filter((t) => t.name.toLowerCase().includes(searchTerm))
  }

  onTalentSelectionChange(talent: Talent): void {
    this.selectedTalent = talent
    this.talentInput = talent.name
  }

  removeSelectedTalent(): void {
    this.selectedTalent = null
    this.talentInput = ""
    this.filteredTalents = this.talents
  }

  isTalentExisting(): boolean {
    return this.talents.some((t) => t.name.toLowerCase() === this.talentInput.toLowerCase())
  }

  addNewTalent(): void {
    const name = this.talentInput.trim()
    if (!name) return
    this.selectedTalent = { name }
    this.talentInput = name
  }

  onProductTypesChange(ids: number[]): void {
    this.selectedProductTypeIds = ids
    const isInitialPreselect =
      !this.productTypesInitialized && this.sameIdLists(ids, this.initialProductTypeIds)
    this.productTypesInitialized = true
    if (isInitialPreselect) return
    this.productTypesDirty = !this.sameIdLists(ids, this.initialProductTypeIds)
  }

  onAudioSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return

    if (!isReplaceableRecordingAudio(file)) {
      input.value = ""
      this.selectedFileName = ""
      this.audioTmpKey = null
      this.audioLength = null
      this.uploading = false
      this.error = REPLACE_AUDIO_ERROR
      return
    }

    this.error = ""
    this.selectedFileName = file.name
    this.uploading = true
    this.audioTmpKey = null
    this.audioLength = null

    // Duration in parallel with S3 upload — do not block the PUT.
    const objectUrl = URL.createObjectURL(file)
    const audio = new Audio()
    audio.preload = "metadata"
    audio.src = objectUrl
    audio.onloadedmetadata = () => {
      // Keep the fractional length like the uploader does: rounding moved e.g. 19.6s (15s rate)
      // to 20s (30s rate) under get_stationrates.derive_spot_length_from_audio.
      this.audioLength = Number.isFinite(audio.duration) ? audio.duration : null
      URL.revokeObjectURL(objectUrl)
    }
    audio.onerror = () => URL.revokeObjectURL(objectUrl)

    this.uploadService.uploadFile(file, "recordings").subscribe({
      next: (result) => {
        this.audioTmpKey = result.tmp_key
        this.uploading = false
      },
      error: (err) => {
        this.uploading = false
        this.audioTmpKey = null
        this.selectedFileName = ""
        this.error =
          err?.message ||
          "Failed to upload audio file. Please try again."
      },
    })
  }

  save(): void {
    this.normalizeName()
    if (!this.canSubmit || this.saving || this.uploading) return
    const recordingId = this.data.recording.id ?? this.data.recording.recording_id
    if (recordingId == null) {
      this.error = "Invalid recording id."
      return
    }

    const payload: {
      name: string
      talentid?: number
      talent_name?: string
      audio_tmp_key?: string
      audiolength?: number
      product_type_ids?: number[]
      is_preemptible_promo?: boolean
      station_id?: number
    } = {
      name: this.name.trim(),
    }

    const selectedId = this.selectedTalent?.id
    if (selectedId != null) {
      payload.talentid = Number(selectedId)
    } else {
      payload.talent_name = (this.selectedTalent?.name || this.talentInput).trim()
    }

    if (this.canReplaceAudio && this.audioTmpKey) {
      if (this.audioLength == null || this.audioLength < 1) {
        this.error = "Audio is too short (minimum 1 second)."
        return
      }
      if (exceedsMaxSpotAudioSeconds(this.audioLength)) {
        this.error = `Audio is too long (maximum ${MAX_SPOT_AUDIO_SECONDS} seconds).`
        return
      }
      payload.audio_tmp_key = this.audioTmpKey
      if (this.audioLength != null) {
        payload.audiolength = this.audioLength
      }
    }

    if (this.canEditStaffFields) {
      payload.is_preemptible_promo = this.isPreemptiblePromo
      if (this.productTypesDirty) {
        payload.product_type_ids = this.selectedProductTypeIds
        payload.station_id = this.data.recording.station_id
      }
    }

    this.saving = true
    this.error = ""
    this.dialogRef.disableClose = true

    this.recordingService.update(Number(recordingId), payload).subscribe({
      next: (res) => {
        this.saving = false
        this.dialogRef.disableClose = false
        const updated = res?.recording || {}
        const closeResult = () => {
          this.dialogRef.close({
            success: true,
            recording: {
              id: Number(recordingId),
              name: updated.name ?? payload.name,
              talentid: updated.talentid ?? payload.talentid,
              audiolength: updated.audiolength ?? payload.audiolength,
              audio_url: updated.audio_url,
              product_types: this.productTypesDirty ? updated.product_types : undefined,
              station_id: this.data.recording.station_id,
              is_preemptible_promo:
                updated.is_preemptible_promo ??
                (this.canEditStaffFields ? this.isPreemptiblePromo : undefined),
            },
          })
        }

        if (this.canEditStaffFields && this.productTypesDirty && !updated.product_types) {
          this.productTypeService.getProductTypes().subscribe({
            next: (catalog) => {
              this.dialogRef.close({
                success: true,
                recording: {
                  id: Number(recordingId),
                  name: updated.name ?? payload.name,
                  talentid: updated.talentid ?? payload.talentid,
                  audiolength: updated.audiolength ?? payload.audiolength,
                  audio_url: updated.audio_url,
                  product_types: catalog.filter((pt) => this.selectedProductTypeIds.includes(pt.id)),
                  station_id: this.data.recording.station_id,
                  is_preemptible_promo:
                    updated.is_preemptible_promo ?? this.isPreemptiblePromo,
                },
              })
            },
            error: () => closeResult(),
          })
          return
        }

        closeResult()
      },
      error: (err) => {
        this.saving = false
        this.dialogRef.disableClose = false
        this.error =
          err?.error?.message ||
          err?.error?.error ||
          "Failed to update recording. Please try again."
      },
    })
  }

  close(): void {
    if (this.saving) return
    this.dialogRef.close()
  }

  private sameIdLists(a: number[], b: number[]): boolean {
    if (a.length !== b.length) return false
    const as = [...a].map(Number).sort((x, y) => x - y)
    const bs = [...b].map(Number).sort((x, y) => x - y)
    return as.every((id, i) => id === bs[i])
  }
}
